import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia, EmptyContent } from "@/components/ui/empty";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { PieChart, Pie, Cell } from "recharts";
import { useDetectionsQuery } from "@/api/v0/detections";
import { useSensorsQueryV1 } from "@/api/v1/sensor-api";
import { useFlightRestrictionZonesQueryV1 } from "@/api/v1/flight-restriction-zone-api";
import { apiTypeToLabel } from "@/lib/zone-type-utils";
import { useGeofencesQueryV1 } from "@/api/v1/geofence-api";
import { 
    RadarIcon, 
    Shield, 
    Activity, 
    Plane, 
    Fence, 
    Clock,
    Settings2,
    BarChart3,
    Navigation,
    Zap,
    Eye,
    Plus,
    Antenna
} from "lucide-react";
import { useMemo } from "react";
import { useLatestDetections } from "@/hooks/use-latest-detections";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Link } from "@tanstack/react-router";

export const DashboardRoute = () => {
    const queryParams = useMemo(() => {
        const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
        return {
            startDate: last24Hours.toISOString(),
        };
    }, []);

    const { data: detectionsResponse, isLoading: detectionsLoading } = useDetectionsQuery(queryParams);
    const { data: sensors, isLoading: sensorsLoading } = useSensorsQueryV1();
    const { data: flightRestrictionZones, isLoading: zonesLoading } = useFlightRestrictionZonesQueryV1();
    const { data: geofences, isLoading: geofencesLoading } = useGeofencesQueryV1();
    const latestDetections = useLatestDetections();

    // Calculate statistics
    const activeDrones = latestDetections.length;
    const activeSensors = sensors?.filter(s => s.status === "active")?.length ?? 0;
    const totalSensors = sensors?.length ?? 0;
    
    // Flight restriction zone statistics
    // Calculate stats from array (v1 API returns array directly)
    const totalZones = flightRestrictionZones?.length ?? 0;
    const activeZones = flightRestrictionZones?.filter(z => {
      const now = new Date();
      const beginTime = new Date(z.effective_begin_time);
      const endTime = z.effective_end_time ? new Date(z.effective_end_time) : null;
      return beginTime <= now && (!endTime || endTime >= now);
    }).length ?? 0;
    const zoneTypeCounts = flightRestrictionZones?.reduce((acc, zone) => {
      const typeLabel = apiTypeToLabel(zone.type);
      acc[typeLabel] = (acc[typeLabel] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) ?? {};

    // Chart data for zone type distribution
    const zoneChartData = Object.entries(zoneTypeCounts).map(([type, count]) => ({
        type,
        count: count as number,
        fill: type === 'Prohibited' ? '#ef4444' : type === 'Restricted' ? '#3b82f6' : '#6b7280'
    })).sort((a, b) => {
      // Sort by type order: Prohibited, Restricted, Danger
      const order = { 'Prohibited': 0, 'Restricted': 1, 'Danger': 2 };
      return (order[a.type as keyof typeof order] ?? 99) - (order[b.type as keyof typeof order] ?? 99);
    });

    const chartConfig = {
        count: {
            label: "Zones",
        },
        type: {
            label: "Zone Type",
        },
    };

    // Geofence statistics
    const totalGeofences = geofences?.length ?? 0;
    const activeGeofences = totalGeofences; // All geofences are considered active since there's no deleted_at field

    const isLoading = detectionsLoading || sensorsLoading || zonesLoading || geofencesLoading;

    return (
        <div className="min-h-screen">
            <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
                <BreadcrumbNav
                    items={[
                        { label: "Home", href: "/" },
                        { label: "Dashboard", href: "/dashboard" },
                    ]}
                    className="mb-6"
                />

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-semibold">Dashboard</h1>
                        <p className="text-sm sm:text-base text-muted-foreground">Monitor airspace activity and manage restrictions</p>
                    </div>
                </div>

                {/* Quick Actions */}
                <Card className="border-2 border-dashed border-muted-foreground/25">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Zap className="h-5 w-5" />
                            Quick Actions
                        </CardTitle>
                        <CardDescription>Get started with common tasks</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                            <Button asChild className="h-auto p-4 sm:p-6 flex flex-col items-center gap-2">
                                <Link to="/map">
                                    <RadarIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                                    <span className="font-semibold text-sm sm:text-base">View Map</span>
                                    <span className="text-xs sm:text-sm text-muted-foreground">Explore airspace</span>
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="h-auto p-4 sm:p-6 flex flex-col items-center gap-2">
                                <Link to="/flight-restriction-zones">
                                    <Shield className="h-6 w-6 sm:h-8 sm:w-8" />
                                    <span className="font-semibold text-sm sm:text-base">Manage Zones</span>
                                    <span className="text-xs sm:text-sm text-muted-foreground">Flight restrictions</span>
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="h-auto p-4 sm:p-6 flex flex-col items-center gap-2">
                                <Link to="/geofences">
                                    <Fence className="h-6 w-6 sm:h-8 sm:w-8" />
                                    <span className="font-semibold text-sm sm:text-base">Geofences</span>
                                    <span className="text-xs sm:text-sm text-muted-foreground">Custom boundaries</span>
                                </Link>
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Key Metrics */}
                <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
                    {/* Flight Restriction Zones */}
                    <Card className="relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16" />
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                            <CardTitle className="text-xs sm:text-sm font-medium">Restriction Zones</CardTitle>
                            <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                        </CardHeader>
                        <CardContent className="pt-2 sm:pt-0">
                            {isLoading ? (
                                <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                            ) : (
                                <div className="text-lg sm:text-2xl font-bold">{activeZones}</div>
                            )}
                            <div className="text-xs text-muted-foreground">
                                {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : `${totalZones} total zones`}
                            </div>
                            {!isLoading && (
                                <div className="mt-1 sm:mt-2">
                                    <Progress value={(activeZones / totalZones) * 100} className="h-1 sm:h-2" />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Geofences */}
                    <Card className="relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full -translate-y-16 translate-x-16" />
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                            <CardTitle className="text-xs sm:text-sm font-medium">Geofences</CardTitle>
                            <Fence className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                        </CardHeader>
                        <CardContent className="pt-2 sm:pt-0">
                            {isLoading ? (
                                <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                            ) : (
                                <div className="text-lg sm:text-2xl font-bold">{activeGeofences}</div>
                            )}
                            <div className="text-xs text-muted-foreground">
                                {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : `${totalGeofences} total geofences`}
                            </div>
                            {!isLoading && (
                                <div className="mt-1 sm:mt-2">
                                    <Progress value={(activeGeofences / totalGeofences) * 100} className="h-1 sm:h-2" />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Active Drones */}
                    <Card className="relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full -translate-y-16 translate-x-16" />
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                            <CardTitle className="text-xs sm:text-sm font-medium">Active Drones</CardTitle>
                            <Plane className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600" />
                        </CardHeader>
                        <CardContent className="pt-2 sm:pt-0">
                            {isLoading ? (
                                <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                            ) : (
                                <div className="text-lg sm:text-2xl font-bold">{activeDrones}</div>
                            )}
                            <div className="text-xs text-muted-foreground">
                                {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : "Currently tracked"}
                            </div>
                            {!isLoading && (
                                <div className="mt-1 sm:mt-2">
                                    <Progress value={activeDrones > 0 ? 100 : 0} className="h-1 sm:h-2" />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Sensors */}
                    <Card className="relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-16 translate-x-16" />
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                            <CardTitle className="text-xs sm:text-sm font-medium">Sensors</CardTitle>
                            <Antenna className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
                        </CardHeader>
                        <CardContent className="pt-2 sm:pt-0">
                            {isLoading ? (
                                <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                            ) : (
                                <div className="text-lg sm:text-2xl font-bold">{activeSensors}</div>
                            )}
                            <div className="text-xs text-muted-foreground">
                                {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : `${totalSensors} total sensors`}
                            </div>
                            {!isLoading && (
                                <div className="mt-1 sm:mt-2">
                                    <Progress value={(activeSensors / totalSensors) * 100} className="h-1 sm:h-2" />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content Tabs */}
                <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
                    <TabsList className="grid w-full grid-cols-3 h-8 sm:h-10">
                        <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
                        <TabsTrigger value="zones" className="text-xs sm:text-sm">Flight Restrictions</TabsTrigger>
                        <TabsTrigger value="activity" className="text-xs sm:text-sm">Recent Activity</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-4 sm:space-y-6">
                        <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
                            {/* Zone Type Distribution */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <BarChart3 className="h-5 w-5" />
                                        Zone Type Distribution
                                    </CardTitle>
                                    <CardDescription>Breakdown of flight restriction zones by type</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {isLoading ? (
                                        <div className="space-y-4">
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-4 w-3/4" />
                                            <Skeleton className="h-4 w-1/2" />
                                        </div>
                                    ) : Object.keys(zoneTypeCounts).length > 0 ? (
                                        <div className="space-y-4">
                                            <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[200px] sm:max-h-[250px]">
                                                <PieChart>
                                                    <Pie
                                                        data={zoneChartData}
                                                        dataKey="count"
                                                        nameKey="type"
                                                        innerRadius={60}
                                                        strokeWidth={5}
                                                    >
                                                        {zoneChartData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                                        ))}
                                                    </Pie>
                                                    <ChartTooltip content={<ChartTooltipContent />} />
                                                    <ChartLegend content={<ChartLegendContent />} />
                                                </PieChart>
                                            </ChartContainer>
                                            <div className="space-y-2">
                                                {Object.entries(zoneTypeCounts).map(([type, count]) => (
                                                    <div key={type} className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div 
                                                                className="w-3 h-3 rounded-full" 
                                                                style={{ 
                                                                    backgroundColor: type === 'Prohibited' ? '#ef4444' : 
                                                                                   type === 'Restricted' ? '#3b82f6' : '#6b7280' 
                                                                }} 
                                                            />
                                                            <span className="text-sm font-medium">{type}</span>
                                                        </div>
                                                        <span className="font-semibold">{count as number}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <Empty>
                                            <EmptyMedia variant="icon">
                                                <Shield className="h-6 w-6" />
                                            </EmptyMedia>
                                            <EmptyHeader>
                                                <EmptyTitle>No zones available</EmptyTitle>
                                                <EmptyDescription>Flight restriction zones will appear here once they're loaded.</EmptyDescription>
                                            </EmptyHeader>
                                        </Empty>
                                    )}
                                </CardContent>
                            </Card>

                            {/* System Status */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Activity className="h-5 w-5" />
                                        System Status
                                    </CardTitle>
                                    <CardDescription>Current system health and connectivity</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">API Status</span>
                                            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                Online
                                            </Badge>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">Data Freshness</span>
                                            <Badge variant="outline">
                                                <Clock className="h-3 w-3 mr-1" />
                                                Real-time
                                            </Badge>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">Coverage</span>
                                            <Badge variant="outline">
                                                <Navigation className="h-3 w-3 mr-1" />
                                                United Kingdom
                                            </Badge>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    <TabsContent value="zones" className="space-y-4 sm:space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Shield className="h-5 w-5" />
                                    Flight Restriction Zones
                                </CardTitle>
                                <CardDescription>Manage and monitor flight restriction zones</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {isLoading ? (
                                    <div className="space-y-4">
                                        {[...Array(3)].map((_, i) => (
                                            <div key={i} className="flex items-center space-x-4">
                                                <Skeleton className="h-12 w-12 rounded" />
                                                <div className="space-y-2">
                                                    <Skeleton className="h-4 w-[200px]" />
                                                    <Skeleton className="h-4 w-[100px]" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : flightRestrictionZones?.length ? (
                                    <div className="space-y-4">
                                        {flightRestrictionZones.slice(0, 5).map((zone) => {
                                            const now = new Date();
                                            const beginTime = new Date(zone.effective_begin_time);
                                            const endTime = zone.effective_end_time ? new Date(zone.effective_end_time) : null;
                                            const isActive = beginTime <= now && (!endTime || endTime >= now);
                                            return (
                                                <div key={zone.id} className="flex items-center justify-between p-4 border rounded-lg">
                                                    <div className="flex items-center space-x-4">
                                                        <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                                                            <Shield className="h-4 w-4 text-blue-600" />
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold">{zone.name}</h4>
                                                            <p className="text-sm text-muted-foreground">
                                                                {apiTypeToLabel(zone.type)} • {zone.designator}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant={isActive ? "default" : "secondary"}>
                                                            {isActive ? "Active" : "Inactive"}
                                                        </Badge>
                                                        <Button variant="ghost" size="sm">
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div className="pt-4">
                                            <Button asChild className="w-full">
                                                <Link to="/flight-restriction-zones">
                                                    View All Zones
                                                </Link>
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <Empty>
                                        <EmptyMedia variant="icon">
                                            <Shield className="h-6 w-6" />
                                        </EmptyMedia>
                                        <EmptyHeader>
                                            <EmptyTitle>No flight restriction zones</EmptyTitle>
                                            <EmptyDescription>Flight restriction zones will appear here once they're loaded.</EmptyDescription>
                                        </EmptyHeader>
                                        <EmptyContent>
                                            <Button asChild>
                                                <Link to="/flight-restriction-zones">
                                                    <Plus className="h-4 w-4 mr-2" />
                                                    View Zones
                                                </Link>
                                            </Button>
                                        </EmptyContent>
                                    </Empty>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="activity" className="space-y-4 sm:space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Clock className="h-5 w-5" />
                                    Recent Activity
                                </CardTitle>
                                <CardDescription>Latest system events and updates</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {isLoading ? (
                                    <div className="space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="flex items-center space-x-4">
                                                <Skeleton className="h-8 w-8 rounded-full" />
                                                <div className="space-y-2 flex-1">
                                                    <Skeleton className="h-4 w-3/4" />
                                                    <Skeleton className="h-3 w-1/2" />
                                                </div>
                                                <Skeleton className="h-4 w-16" />
                                            </div>
                                        ))}
                                    </div>
                                ) : detectionsResponse?.detections?.length ? (
                                    <div className="space-y-4">
                                        {detectionsResponse.detections.slice(0, 5).map((detection) => (
                                            <div key={detection.id} className="flex items-center justify-between p-4 border rounded-lg">
                                                <div className="flex items-center space-x-4">
                                                    <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900">
                                                        <Plane className="h-4 w-4 text-orange-600" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold">{detection.drone_identifier}</h4>
                                                        <p className="text-sm text-muted-foreground">
                                                            Detected by {sensors?.find(s => s.slug === detection.sensor_id || s.organisation_id.toString() === detection.sensor_id)?.name ?? detection.sensor_id}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-sm text-muted-foreground">
                                                    {new Date(detection.detection_time).toLocaleTimeString()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Empty>
                                        <EmptyMedia variant="icon">
                                            <Activity className="h-6 w-6" />
                                        </EmptyMedia>
                                        <EmptyHeader>
                                            <EmptyTitle>No recent activity</EmptyTitle>
                                            <EmptyDescription>Drone detections and system events will appear here.</EmptyDescription>
                                        </EmptyHeader>
                                    </Empty>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Onboarding for users without sensors */}
                {!isLoading && totalSensors === 0 && (
                    <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
                        <Settings2 className="h-4 w-4" />
                        <AlertDescription>
                            <div className="space-y-2">
                                <p className="font-semibold">Welcome to Stratos!</p>
                                <p>
                                    You don't have any sensors set up yet. While you can still view flight restriction zones and manage geofences, 
                                    you'll need sensors to track drone activity. 
                                    <Link to="/sensors" className="text-blue-600 hover:underline ml-1">
                                        Set up your first sensor
                                    </Link> to get started with drone detection.
                                </p>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}
            </div>
        </div>
    );
};
